import { config, endpoints, postLogoutUri, redirectUri } from '../config';

const KEYS = {
  verifier: 'r2_pkce_verifier',
  state:    'r2_pkce_state',
  access:   'r2_access_token',
  refresh:  'r2_refresh_token',
  expires:  'r2_expires_at',
} as const;

/**
 * Token-Speicher: localStorage, damit Access-/Refresh-Token einen Reload bzw.
 * einen Browser-Prozess-Neustart überleben. Hintergrund: Auf Mobilgeräten
 * (Android/iOS) wird beim Öffnen der Kamera (`<input capture>`) unter
 * Speicherdruck der Web-Prozess verworfen und die Seite neu geladen — mit
 * sessionStorage wäre der Benutzer danach komplett ausgeloggt. Mit localStorage
 * überlebt zumindest das Refresh-Token, und der nächste Aufruf holt transparent
 * ein neues Access-Token (kein erneuter OAuth-Login nötig).
 *
 * PKCE-Verifier/State bleiben bewusst in sessionStorage (nur während des
 * Login-Redirects relevant, kein Persistenzbedarf).
 */
const tokenStore = localStorage;

function base64UrlEncode(bytes: ArrayBuffer | Uint8Array): string {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let bin = '';
  for (let i = 0; i < arr.length; i++) bin += String.fromCharCode(arr[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(48);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

export async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
}

function generateState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

export async function startLogin(): Promise<void> {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = generateState();

  sessionStorage.setItem(KEYS.verifier, verifier);
  sessionStorage.setItem(KEYS.state, state);

  const params = new URLSearchParams({
    response_type:         'code',
    client_id:             config.clientId,
    redirect_uri:          redirectUri,
    scope:                 config.scope,
    state,
    code_challenge:        challenge,
    code_challenge_method: 'S256',
  });

  window.location.assign(`${endpoints.authorize}?${params.toString()}`);
}

export class CallbackError extends Error {
  constructor(message: string, readonly code: 'state_mismatch' | 'token_failed') {
    super(message);
  }
}

export async function handleCallback(code: string, state: string): Promise<void> {
  const storedState    = sessionStorage.getItem(KEYS.state);
  const storedVerifier = sessionStorage.getItem(KEYS.verifier);

  sessionStorage.removeItem(KEYS.state);
  sessionStorage.removeItem(KEYS.verifier);

  if (!storedState || !storedVerifier || storedState !== state) {
    throw new CallbackError('State mismatch', 'state_mismatch');
  }

  const body = new URLSearchParams({
    grant_type:    'authorization_code',
    code,
    code_verifier: storedVerifier,
    redirect_uri:  redirectUri,
    client_id:     config.clientId,
  });

  const resp = await fetch(endpoints.token, {
    method:  'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body:    body.toString(),
  });

  if (!resp.ok) {
    throw new CallbackError(`Token exchange failed (${resp.status})`, 'token_failed');
  }

  const json = (await resp.json()) as {
    access_token: string;
    refresh_token?: string;
    expires_in: number;
    token_type: string;
  };

  tokenStore.setItem(KEYS.access, json.access_token);
  if (json.refresh_token) tokenStore.setItem(KEYS.refresh, json.refresh_token);
  tokenStore.setItem(KEYS.expires, String(Date.now() + json.expires_in * 1000));
}

export interface CurrentUser {
  id: number;
  userName: string;
  vn: string | null;
  nn: string | null;
  mail: string | null;
}

export async function getCurrentUser(): Promise<CurrentUser> {
  const token = await getAccessToken();
  if (!token) {
    throw new Error('no_token');
  }
  const resp = await fetch(endpoints.userinfo, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (resp.status === 401) {
    // Refresh hat nicht geholfen oder Server hat das Token aktiv widerrufen.
    // Nur lokal aufräumen, kein SSO-Logout — der Caller entscheidet, ob er
    // den User auf den Login-Knopf zurückwirft.
    logout('none');
    throw new Error('unauthorized');
  }
  if (!resp.ok) {
    throw new Error(`userinfo_failed_${resp.status}`);
  }
  return (await resp.json()) as CurrentUser;
}

/**
 * "Angemeldet" heißt: entweder haben wir ein gültiges Access-Token, oder wir
 * haben zumindest ein Refresh-Token in der Hinterhand. Im zweiten Fall holt
 * der nächste `getAccessToken()`-Aufruf das frische Access-Token transparent.
 */
export function isAuthenticated(): boolean {
  const token   = tokenStore.getItem(KEYS.access);
  const expires = tokenStore.getItem(KEYS.expires);
  if (token && expires && Date.now() < parseInt(expires, 10)) return true;
  return !!tokenStore.getItem(KEYS.refresh);
}

let pendingRefresh: Promise<boolean> | null = null;

/**
 * Tauscht das gespeicherte Refresh-Token gegen ein neues Access-Token. Bei
 * Erfolg werden Access-Token, ggf. neues Refresh-Token (Rotation) und das
 * neue Ablaufdatum in den tokenStore (localStorage) geschrieben. Bei Misserfolg werden
 * die Tokens lokal gelöscht — der Refresh-Token gilt damit als verbrannt.
 *
 * Concurrent calls werden zusammengefasst, damit nicht mehrere parallele
 * API-Aufrufe den Refresh-Token mehrfach gegen den Server schicken.
 */
async function refreshAccessToken(): Promise<boolean> {
  const refresh = tokenStore.getItem(KEYS.refresh);
  if (!refresh) return false;
  if (pendingRefresh) return pendingRefresh;

  pendingRefresh = (async () => {
    try {
      const body = new URLSearchParams({
        grant_type:    'refresh_token',
        refresh_token: refresh,
        client_id:     config.clientId,
      });
      const resp = await fetch(endpoints.token, {
        method:  'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body:    body.toString(),
      });
      if (!resp.ok) {
        tokenStore.removeItem(KEYS.access);
        tokenStore.removeItem(KEYS.refresh);
        tokenStore.removeItem(KEYS.expires);
        return false;
      }
      const json = (await resp.json()) as {
        access_token: string;
        refresh_token?: string;
        expires_in: number;
        token_type: string;
      };
      tokenStore.setItem(KEYS.access, json.access_token);
      if (json.refresh_token) tokenStore.setItem(KEYS.refresh, json.refresh_token);
      tokenStore.setItem(KEYS.expires, String(Date.now() + json.expires_in * 1000));
      return true;
    } catch {
      return false;
    } finally {
      pendingRefresh = null;
    }
  })();

  return pendingRefresh;
}

/**
 * Liefert ein gültiges Access-Token, oder `null` wenn weder ein gültiges
 * Access-Token noch ein einlösbares Refresh-Token vorhanden ist.
 *
 * 30 s Puffer vor dem nominalen Ablauf — sonst kann ein Request mitten in
 * der Übertragung in 401 laufen, wenn das Token genau dann abläuft.
 */
export async function getAccessToken(): Promise<string | null> {
  const token   = tokenStore.getItem(KEYS.access);
  const expires = tokenStore.getItem(KEYS.expires);
  if (token && expires && Date.now() < parseInt(expires, 10) - 30_000) {
    return token;
  }
  const ok = await refreshAccessToken();
  return ok ? tokenStore.getItem(KEYS.access) : null;
}

/**
 * Lokal alle Tokens verwerfen. Optional zusätzlich am OAuth-Server abmelden
 * (RP-Initiated Logout) — das ist die richtige Variante für den Abmelden-Button,
 * damit ein anderer Benutzer sich danach mit eigenen Credentials anmelden kann.
 *
 * `mode`:
 *   - 'sso'    (default): Tokens löschen + Redirect zu /logout am OAuth-Server,
 *              dieser killt die Server-Session und schickt den Browser nach `/`
 *              zurück. Beim nächsten Anmelde-Klick muss frisch eingeloggt werden.
 *   - 'local': Tokens löschen, nur lokales /-Redirect. Server-Session bleibt
 *              bestehen — beim nächsten Anmelde-Klick wird man stillschweigend
 *              wieder eingeloggt. Sinnvoll z.B. nach Token-Expiry-Recovery.
 *   - 'none':  Tokens löschen ohne Navigation (für Aufräumen in Error-Handlern).
 */
export function logout(mode: 'sso' | 'local' | 'none' = 'sso'): void {
  tokenStore.removeItem(KEYS.access);
  tokenStore.removeItem(KEYS.refresh);
  tokenStore.removeItem(KEYS.expires);
  sessionStorage.removeItem(KEYS.verifier);
  sessionStorage.removeItem(KEYS.state);

  if (mode === 'none') return;

  if (mode === 'sso') {
    const params = new URLSearchParams({
      client_id:                config.clientId,
      post_logout_redirect_uri: postLogoutUri,
    });
    window.location.assign(`${endpoints.logout}?${params.toString()}`);
    return;
  }

  window.location.assign('/');
}
